import { ApolloError } from '@apollo/client';
import { Button, makeStyles, TextField, Typography } from '@material-ui/core';
import red from '@material-ui/core/colors/red';
import React, { SyntheticEvent, useState } from 'react';

import { BoardDetailsFragment, UpdateBoardInput } from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { PlexName } from '../../common/PlexName';
import { DialogActions } from '../DialogActions';
import { DialogTopBar } from '../DialogTopBar';
import { CreateOrEditDialogTitle } from '../post/CreateOrEditDialogTitle';
import { BoardActions } from './BoardActions';

type CommonProps = {
  board: Omit<BoardDetailsFragment, 'id' | 'position'>;
  onSave: (board: UpdateBoardInput) => void;
  saveLoading: boolean;
  saveError: ApolloError | undefined;
};

type CustomProps = {
  onDelete: (faqDestinationBoard: string, deleteAllFaqs: boolean) => void;
  boardId: string;
};

type Props =
  | (CommonProps & { mode: 'update' } & Required<CustomProps>)
  | (CommonProps & { mode: 'create' } & Partial<CustomProps>);

export type BoardFormData = {
  name: string;
  description: string;
};

const NAME_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 280;

export function BaseBoardDialog(props: Props): React.ReactElement {
  const { mode, board, boardId, saveLoading, saveError, onSave, onDelete } = props;
  const classes = useStyles();
  const [state, setState] = useState<BoardFormData>({
    name: board.name,
    description: board.description || '',
  });

  const handleBoardFormChange = React.useCallback(
    (changeCallback: (prevFormData: BoardFormData) => BoardFormData): void => {
      setState((prevState) => (prevState !== undefined ? changeCallback(prevState) : prevState));
    },
    [setState]
  );

  const handleSave = (): void => {
    if (state != null) {
      onSave({
        name: state.name,
        description: state.description,
        // TODO use deletedBoardTarget
      });
    }
  };

  const handleNameChange = (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const name = event.currentTarget.value;
    handleBoardFormChange((prevData) => ({
      ...prevData,
      name,
    }));
  };

  const handleDescriptionChange = (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const description = event.currentTarget.value;
    handleBoardFormChange((prevData) => ({
      ...prevData,
      description,
    }));
  };

  const saveEnabled = board != null && !saveLoading;

  // TODO: form input validation
  // FIXME: Dialog usage causes a console warning, more details in https://github.com/mui-org/material-ui/issues/13394
  return (
    <div className={classes.root}>
      <DialogTopBar>
        <PlexName name={board.plex.name} color={board.plex.color} className={classes.plexName} />
      </DialogTopBar>

      <CreateOrEditDialogTitle />

      {state != null && (
        <div className={classes.contentContainer}>
          <div className={classes.content}>
            <TextField
              multiline
              className={classes.formInput}
              autoComplete={'off'}
              margin={'dense'}
              type={'text'}
              label={'Name'}
              value={state.name}
              onChange={handleNameChange}
              helperText={`${state.name.length} / ${NAME_MAX_LENGTH}`}
            />

            <TextField
              multiline
              className={classes.formInput}
              autoComplete={'off'}
              margin={'dense'}
              type={'text'}
              label={'Description'}
              value={state.description}
              onChange={handleDescriptionChange}
              helperText={`${state.description.length} / ${DESCRIPTION_MAX_LENGTH}`}
            />

            {mode === 'update' && boardId != null && (
              <BoardActions board={{ ...board, id: boardId }} onDelete={onDelete} />
            )}
          </div>
        </div>
      )}

      <DialogActions
        cta={
          <Button disabled={!saveEnabled} onClick={handleSave} color="primary" variant="contained">
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        }
      >
        {/* TODO: Make error state prettier */}
        {saveError != null && (
          <Typography className={classes.errorState} variant="body1">
            Could not save board due to error {saveError.message}
          </Typography>
        )}
      </DialogActions>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    overflow: 'hidden',
    flexDirection: 'column',
    height: '100%',
  },
  plexName: {
    marginLeft: spacings.xlarge,
    marginTop: spacings.normal,
    marginBottom: spacings.normal,
  },
  contentContainer: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    paddingRight: spacings.xxlarge * 2,
    paddingBottom: spacings.xlarge,
    overflow: 'auto',
    flex: 1,
    width: '100%',
  },
  errorState: {
    color: red[600],
  },
  formInput: {
    display: 'flex',
    marginTop: spacings.xsmall,
    marginLeft: spacings.xlarge,
    marginRight: spacings.xlarge,
  },
  deleteBtn: {
    display: 'flex',
    marginTop: spacings.normal,
    marginLeft: spacings.xlarge,
  },
}));
